import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { strategies } from '@/mocks/strategies';
import SeoHead from '@/components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.diariode1trader.com';

const estrategiasSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Estratégias de Trading de Futuros',
  description: 'Estratégias comprovadas de trading de futuros para prop firms: EMA Cross, EMA 200 Bounce, 1H Candle, Impulse Entry, HH/LL e VWAP.',
  url: `${siteUrl}/estrategias`,
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'EMA Cross Scalping', url: `${siteUrl}/estrategias/scalping` },
    { '@type': 'ListItem', position: 2, name: 'EMA 200 Bounce', url: `${siteUrl}/estrategias/day-trading` },
    { '@type': 'ListItem', position: 3, name: '1H Candle Strategy', url: `${siteUrl}/estrategias/swing-trading` },
    { '@type': 'ListItem', position: 4, name: 'Impulse Entry', url: `${siteUrl}/estrategias/breakout` },
    { '@type': 'ListItem', position: 5, name: 'HH/LL Market Structure', url: `${siteUrl}/estrategias/trend-following` },
    { '@type': 'ListItem', position: 6, name: 'VWAP Strategy', url: `${siteUrl}/estrategias/orderflow` },
  ],
};

export default function EstrategiasPage() {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Estratégias de Trading de Futuros | Diário de 1 Trader"
        description="Aprende as melhores estratégias de trading de futuros: EMA Cross, EMA 200 Bounce, 1H Candle, Impulse Entry, HH/LL e VWAP. Análises completas com regras de entrada, gestão e exemplos."
        keywords="estratégias trading futuros, EMA, VWAP, impulse entry, HH LL, prop firms"
        canonical="/estrategias"
        schema={estrategiasSchema}
      />
      <Navbar />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-semibold px-4 py-2 rounded-full mb-4">
            <i className="ri-flashlight-line"></i>
            Estratégias Comprovadas
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-[#0d1f3c] mb-4">Estratégias de Trading de Futuros</h1>
          <p className="text-slate-500 max-w-2xl mx-auto">Análises detalhadas de cada metodologia, com pontos fortes, pontos fracos, indicadores e compatibilidade com prop firms.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {strategies.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-blue-100 overflow-hidden hover:border-blue-300 transition-all group">
              <div className="relative h-48">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex gap-2 mb-2">
                    <span className="bg-blue-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">{s.timeframe}</span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.risk === 'Alto' ? 'bg-red-500/80 text-white' : s.risk === 'Médio' ? 'bg-yellow-500/80 text-white' : 'bg-green-500/80 text-white'}`}>{s.risk}</span>
                  </div>
                  <h3 className="text-white font-bold text-xl">{s.name}</h3>
                </div>
              </div>
              <div className="p-5">
                <p className="text-slate-600 text-sm mb-4 leading-relaxed">{s.description.slice(0, 140)}...</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                    <div className="text-xs text-slate-400">Win Rate</div>
                    <div className="font-bold text-blue-700 text-sm">{s.winRate}</div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                    <div className="text-xs text-slate-400">Risco/Retorno</div>
                    <div className="font-bold text-blue-700 text-sm">{s.riskReward}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link
                    to={`/estrategias/${s.id}`}
                    className="flex-1 bg-blue-600 text-white hover:bg-blue-700 font-semibold py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap text-center"
                  >
                    Ver Artigo Completo
                  </Link>
                  <button
                    onClick={() => setSelected(selected === s.id ? null : s.id)}
                    className="border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className={selected === s.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
                  </button>
                </div>
                {selected === s.id && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <h4 className="font-bold text-green-700 text-sm mb-2 flex items-center gap-1"><i className="ri-check-double-line"></i> Pontos Fortes</h4>
                      <ul className="space-y-1">{s.pros.map(p => <li key={p} className="text-xs text-slate-600 flex items-start gap-1.5"><i className="ri-check-line text-green-500 mt-0.5 shrink-0"></i>{p}</li>)}</ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-red-600 text-sm mb-2 flex items-center gap-1"><i className="ri-close-circle-line"></i> Pontos Fracos</h4>
                      <ul className="space-y-1">{s.cons.map(c => <li key={c} className="text-xs text-slate-600 flex items-start gap-1.5"><i className="ri-close-line text-red-400 mt-0.5 shrink-0"></i>{c}</li>)}</ul>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-3">
                      <div className="text-xs text-slate-400 mb-1">Indicadores Principais</div>
                      <div className="flex flex-wrap gap-1.5">{s.indicators.map(ind => <span key={ind} className="bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full">{ind}</span>)}</div>
                    </div>
                    <div className="text-xs text-slate-500 bg-slate-50 rounded-xl p-3">
                      <strong>Compatibilidade Prop Firms:</strong> {s.propFirmCompatibility}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
